import type * as React from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export const Dialog = BaseDialog.Root;

export function DialogTrigger(props: BaseDialog.Trigger.Props) {
  return <BaseDialog.Trigger data-slot='dialog-trigger' {...props} />;
}

/*
 * Enter-only animation: Base UI unmounts the popup on close unless
 * ending-style transitions are declared, so close is instant by design.
 */
export const dialogContentVariants = cva(
  /* A margin on a phone, so the rounded corners never meet the screen edge. */
  'fixed left-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 rounded-xl bg-surface shadow-modal outline-none animate-pop',
  {
    variants: {
      size: {
        sm: 'max-w-[300px]',
        md: 'max-w-[460px]',
        lg: 'max-w-[640px]',
        /*
         * The biggest thing that still reads as a modal: a generous margin all
         * round and a capped width, so the dimmed page stays visible behind it
         * and the window underneath keeps being the place the user is. It is a
         * column like the full-window surfaces are, because what goes in it is a
         * header with something scrolling under it and not a paragraph.
         *
         * The margin is what the centred layout already does — half the window
         * minus half the popup — so nothing about the positioning changes.
         */
        full: 'flex h-[calc(100dvh-6rem)] w-[calc(100dvw-6rem)] max-w-[1200px] flex-col',
      },
      /*
       * How the popup meets a viewport it does not fit in.
       *
       * `centered` is a decision: a question with two buttons, short enough to
       * read at a glance, held in the middle of the screen.
       *
       * `scroll` is a document — a report to be read from top to bottom. It
       * hangs from a fixed margin at the top and scrolls as one column, so
       * whatever ends the dialog is reached by reading on rather than sitting
       * pinned over the last paragraph.
       *
       * Either way the popup never outgrows the viewport: a `centered` popup
       * that fills up (a paste, a long list) caps just short of the window
       * edges and scrolls, so its footer is always reachable.
       */
      layout: {
        centered: 'top-1/2 -translate-y-1/2 max-h-[calc(100dvh-4rem)] overflow-y-auto',
        scroll: 'top-15 max-h-[calc(100dvh-120px)] overflow-y-auto',
      },
    },
    defaultVariants: {
      size: 'sm',
      layout: 'centered',
    },
  },
);

export interface DialogContentProps
  extends
    Omit<BaseDialog.Popup.Props, 'className' | 'style'>,
    VariantProps<typeof dialogContentVariants> {
  className?: string;
}

export function DialogContent({
  size,
  layout,
  className,
  children,
  ...props
}: DialogContentProps) {
  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop
        data-slot='dialog-backdrop'
        className='fixed inset-0 z-50 bg-scrim backdrop-blur-scrim'
      />
      <BaseDialog.Popup
        data-slot='dialog-content'
        className={cn(dialogContentVariants({ size, layout }), className)}
        {...props}
      >
        {children}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  );
}

export type DialogHeaderProps = Omit<React.ComponentProps<'div'>, 'style'>;

export function DialogHeader({ className, ...props }: DialogHeaderProps) {
  return (
    <div
      data-slot='dialog-header'
      className={cn(
        'flex items-start justify-between gap-3 border-b border-fill px-5 pt-4.5 pb-3.5',
        className,
      )}
      {...props}
    />
  );
}

export interface DialogTitleProps extends Omit<
  BaseDialog.Title.Props,
  'className' | 'style'
> {
  className?: string;
}

export function DialogTitle({ className, ...props }: DialogTitleProps) {
  return (
    <BaseDialog.Title
      data-slot='dialog-title'
      className={cn('text-md font-bold text-ink', className)}
      {...props}
    />
  );
}

export interface DialogDescriptionProps extends Omit<
  BaseDialog.Description.Props,
  'className' | 'style'
> {
  className?: string;
}

export function DialogDescription({ className, ...props }: DialogDescriptionProps) {
  return (
    <BaseDialog.Description
      data-slot='dialog-description'
      className={cn('text-sm text-ink-muted', className)}
      {...props}
    />
  );
}

export type DialogFooterProps = Omit<React.ComponentProps<'div'>, 'style'>;

export function DialogFooter({ className, ...props }: DialogFooterProps) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn('flex justify-end gap-2 px-5 pt-3.5 pb-5', className)}
      {...props}
    />
  );
}

export interface DialogCloseProps extends Omit<
  BaseDialog.Close.Props,
  'className' | 'style' | 'children'
> {
  className?: string;
}

export function DialogClose({
  className,
  'aria-label': ariaLabel = 'Close',
  ...props
}: DialogCloseProps) {
  return (
    <BaseDialog.Close
      data-slot='dialog-close'
      aria-label={ariaLabel}
      className={cn(
        'flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-fill hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        className,
      )}
      {...props}
    >
      <X className='size-4' aria-hidden='true' />
    </BaseDialog.Close>
  );
}
